import { useEffect, useState } from 'react';

import { api } from '../api/client';
import type { Account } from '../types';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export function BalanceSheet() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [asOf, setAsOf] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    async function fetchAccounts() {
      try {
        const data = await api.accounts.list();
        setAccounts(data);
      } catch (error) {
        console.error('Failed to fetch accounts:', error);
      } finally {
        setLoading(false);
      }
    }
    void fetchAccounts();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(Math.abs(amount));
  };

  // Group accounts by type
  const assets = accounts.filter(a => a.type === 'ASSET');
  const liabilities = accounts.filter(a => a.type === 'LIABILITY');
  const equity = accounts.filter(a => a.type === 'EQUITY');

  const totalAssets = assets.reduce((sum, a) => sum + a.balance, 0);
  const totalLiabilities = liabilities.reduce((sum, a) => sum + Math.abs(a.balance), 0);
  const totalEquity = equity.reduce((sum, a) => sum + Math.abs(a.balance), 0);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin text-3xl mb-4"><i className="bi bi-arrow-repeat"></i></div>
        <p className="text-muted-foreground">Loading balance sheet...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pb-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Balance Sheet</h1>
          <p className="text-sm text-muted-foreground">Statement of Financial Position</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            type="date"
            className="px-3 py-2 text-sm border border-input rounded-md bg-background"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
          />
          <Button variant="outline" size="sm">Export PDF</Button>
          <Button variant="default" size="sm">Print</Button>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        As of {new Date(asOf).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Assets */}
        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <i className="bi bi-wallet"></i> Assets
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="py-2 px-3 text-left font-medium text-muted-foreground">Account</th>
                      <th className="py-2 px-3 text-right font-medium text-muted-foreground">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assets.map((account) => (
                      <tr key={account.id} className="border-b border-border hover:bg-muted/50">
                        <td className="py-2 px-3">
                          <code className="text-xs bg-muted px-1.5 py-0.5 rounded mr-2">{account.code}</code>
                          {account.name}
                        </td>
                        <td className="py-2 px-3 text-right font-mono">
                          {formatCurrency(account.balance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="font-semibold bg-muted/50">
                      <td className="py-2 px-3">Total Assets</td>
                      <td className="py-2 px-3 text-right font-mono">{formatCurrency(totalAssets)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Liabilities & Equity */}
        <div className="flex flex-col gap-6">
          {/* Liabilities */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <i className="bi bi-credit-card"></i> Liabilities
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="py-2 px-3 text-left font-medium text-muted-foreground">Account</th>
                      <th className="py-2 px-3 text-right font-medium text-muted-foreground">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {liabilities.map((account) => (
                      <tr key={account.id} className="border-b border-border hover:bg-muted/50">
                        <td className="py-2 px-3">
                          <code className="text-xs bg-muted px-1.5 py-0.5 rounded mr-2">{account.code}</code>
                          {account.name}
                        </td>
                        <td className="py-2 px-3 text-right font-mono">
                          {formatCurrency(account.balance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="font-semibold bg-muted/50">
                      <td className="py-2 px-3">Total Liabilities</td>
                      <td className="py-2 px-3 text-right font-mono">{formatCurrency(totalLiabilities)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Equity */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <i className="bi bi-piggy-bank"></i> Equity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="py-2 px-3 text-left font-medium text-muted-foreground">Account</th>
                      <th className="py-2 px-3 text-right font-medium text-muted-foreground">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {equity.map((account) => (
                      <tr key={account.id} className="border-b border-border hover:bg-muted/50">
                        <td className="py-2 px-3">
                          <code className="text-xs bg-muted px-1.5 py-0.5 rounded mr-2">{account.code}</code>
                          {account.name}
                        </td>
                        <td className="py-2 px-3 text-right font-mono">
                          {formatCurrency(account.balance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="font-semibold bg-muted/50">
                      <td className="py-2 px-3">Total Equity</td>
                      <td className="py-2 px-3 text-right font-mono">{formatCurrency(totalEquity)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Summary */}
          <Card className="bg-muted/50">
            <CardContent className="pt-6">
              <div className="flex justify-between items-center">
                <span className="font-semibold">Total Liabilities + Equity</span>
                <span className="text-xl font-mono">{formatCurrency(totalLiabilities + totalEquity)}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Balance Check */}
      <Card className={Math.abs(totalAssets - (totalLiabilities + totalEquity)) < 0.01 ? 'border-green-500' : 'border-red-500'}>
        <CardContent className="pt-6">
          {Math.abs(totalAssets - (totalLiabilities + totalEquity)) < 0.01 ? (
            <div className="flex items-center gap-2 text-green-600">
              <i className="bi bi-check-circle text-xl"></i>
              <span>Balance Sheet is balanced (Assets = Liabilities + Equity)</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-red-600">
              <i className="bi bi-exclamation-triangle-fill text-xl"></i>
              <span>Balance Sheet is not balanced (Difference: {formatCurrency(totalAssets - totalLiabilities - totalEquity)})</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}